export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly body?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

function resolveApiBaseUrl(): string {
  const raw = (import.meta.env.VITE_API_BASE_URL as string | undefined)?.trim() ?? '';
  return raw.replace(/\/+$/, '');
}

function buildUrl(path: string): string {
  const base = resolveApiBaseUrl();
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return base ? `${base}${normalizedPath}` : normalizedPath;
}

/** Build API URL (relative in dev with Vite proxy, or absolute when VITE_API_BASE_URL is set). */
export function apiUrl(path: string): string {
  return buildUrl(path);
}

function htmlInsteadOfJsonMessage(): string {
  if (!import.meta.env.DEV) {
    return 'שגיאה בחיבור לשרת';
  }
  return (
    'שגיאה בחיבור לשרת — ה-API החזיר דף HTML במקום JSON. ' +
    'ודאו ש-Nest רץ (npm run dev:up מהשורש) וש-port 3000 הוא MondayGIS, ' +
    'או הגדירו VITE_DEV_PROXY_TARGET ב-.env.development.local.'
  );
}

export async function readJsonBody<T>(res: Response): Promise<T> {
  const text = await res.text();
  if (!text) {
    return undefined as T;
  }
  try {
    return JSON.parse(text) as T;
  } catch {
    if (text.trimStart().startsWith('<')) {
      throw new ApiError(htmlInsteadOfJsonMessage(), res.status, text.slice(0, 200));
    }
    throw new ApiError('Invalid JSON response', res.status, text.slice(0, 200));
  }
}

export async function apiFetch<T>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const headers = new Headers(init.headers);
  if (!headers.has('Accept')) {
    headers.set('Accept', 'application/json');
  }
  if (init.body != null && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  const res = await fetch(buildUrl(path), {
    ...init,
    headers,
  });

  if (!res.ok) {
    let body: unknown;
    try {
      body = await readJsonBody<unknown>(res);
    } catch (err) {
      if (err instanceof ApiError) throw err;
      body = undefined;
    }
    const message =
      typeof body === 'object' &&
      body !== null &&
      'message' in body &&
      typeof (body as { message: unknown }).message === 'string'
        ? (body as { message: string }).message
        : res.statusText || `Request failed (${res.status})`;
    throw new ApiError(message, res.status, body);
  }

  if (res.status === 204) {
    return undefined as T;
  }

  return readJsonBody<T>(res);
}

export function apiPost<T>(path: string, body?: unknown): Promise<T> {
  return apiFetch<T>(path, {
    method: 'POST',
    body: body === undefined ? undefined : JSON.stringify(body),
  });
}

export function apiPatch<T>(path: string, body?: unknown): Promise<T> {
  return apiFetch<T>(path, {
    method: 'PATCH',
    body: body === undefined ? undefined : JSON.stringify(body),
  });
}

export function apiGet<T>(path: string): Promise<T> {
  return apiFetch<T>(path, { method: 'GET' });
}

/** POST and return binary body (e.g. application/pdf). */
export async function apiPostBlob(
  path: string,
  body?: unknown,
  accept = 'application/pdf',
): Promise<Blob> {
  const headers = new Headers({ 'Content-Type': 'application/json', Accept: accept });
  const res = await fetch(buildUrl(path), {
    method: 'POST',
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
  });

  if (!res.ok) {
    let payload: unknown;
    try {
      payload = await res.json();
    } catch {
      payload = undefined;
    }
    const message =
      typeof payload === 'object' &&
      payload !== null &&
      'message' in payload &&
      typeof (payload as { message: unknown }).message === 'string'
        ? (payload as { message: string }).message
        : res.statusText || `Request failed (${res.status})`;
    throw new ApiError(message, res.status, payload);
  }

  return res.blob();
}
